import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPayments } from '../../api/adminPaymentsApi';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'created', label: 'Created' },
  { value: 'authorized', label: 'Authorized' },
  { value: 'captured', label: 'Captured' },
  { value: 'pending', label: 'Pending' },
  { value: 'initiated', label: 'Initiated' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'partially_refunded', label: 'Partially refunded' },
  { value: 'refunded', label: 'Refunded' },
];

const badgeStyles = {
  created: 'bg-amber-50 text-amber-700 ring-amber-100',
  authorized: 'bg-amber-50 text-amber-700 ring-amber-100',
  pending: 'bg-amber-50 text-amber-700 ring-amber-100',
  initiated: 'bg-amber-50 text-amber-700 ring-amber-100',
  captured: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  paid: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  failed: 'bg-rose-50 text-rose-700 ring-rose-100',
  refunded: 'bg-slate-100 text-slate-700 ring-slate-200',
  partially_refunded: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || 'bg-slate-100 text-slate-700 ring-slate-200';
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  );
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatAmount = (currency, amount, amountPaise) => {
  const paise = Number(amountPaise);
  if (Number.isFinite(paise) && amountPaise !== null && amountPaise !== undefined) {
    const value = paise / 100;
    return `${currency || ''} ${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
  }
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    const label = `${currency || ''} ${amount || ''}`.trim();
    return label || 'N/A';
  }
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const getBookingLabel = (row) => row.booking_code || (row.booking_id ? `#${row.booking_id}` : 'N/A');

const getGuestName = (row) => row.customer_name || 'Guest';
const getGuestEmail = (row) => row.customer_email || 'N/A';
const getHostName = (row) => row.host_name || 'Host';
const getHostEmail = (row) => row.host_email || 'N/A';
const getReference = (row) => row.payment_ref || row.reference || row.txn_id || 'N/A';
const getGatewayPaymentId = (row) => row.gateway_payment_id || 'N/A';
const getGatewayOrderId = (row) => row.gateway_order_id || 'N/A';
const getProvider = (row) => {
  const label = [row.gateway_provider || row.provider, row.method].filter(Boolean).join(' - ');
  return label || 'manual';
};

const AdminTransactions = ({ token }) => {
  const [filters, setFilters] = useState({ status: '', q: '', date_from: '', date_to: '' });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await listPayments(token, { ...filters, page, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({
        page: res.page || page,
        limit: res.limit || pagination.limit,
        total: res.total || 0,
      });
    } catch (err) {
      setError(err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  const applyFilters = () => {
    load(1);
  };

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Payment Transactions</h1>
          <p className="text-sm text-slate-600">Track guest payments across the platform.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search booking, guest, host, or gateway id"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyFilters();
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Booking</th>
              <th className="px-4 py-3">Guest</th>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={8} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={8} className="px-4 py-3 text-slate-500">
                  No payments found.
                </td>
              </tr>
            )}
            {items.map((payment) => (
              <tr key={payment.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">#{payment.id}</div>
                  <div className="text-xs text-slate-500">Ref: {getReference(payment)}</div>
                  <div className="text-[11px] text-slate-400">Gateway payment: {getGatewayPaymentId(payment)}</div>
                  <div className="text-[11px] text-slate-400">Gateway order: {getGatewayOrderId(payment)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">{getBookingLabel(payment)}</div>
                  <div className="text-xs text-slate-500">{payment.property_title || 'Property'}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">{getGuestName(payment)}</div>
                  <div className="text-xs text-slate-500">{getGuestEmail(payment)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">{getHostName(payment)}</div>
                  <div className="text-xs text-slate-500">{getHostEmail(payment)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">
                    {formatAmount(payment.currency || payment.booking_currency, payment.amount, payment.amount_paise)}
                  </div>
                  <div className="text-xs text-slate-500">{getProvider(payment)}</div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge value={payment.status} />
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(payment.created_at)}</td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/bookings/${payment.booking_id}`)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    View booking
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pageCount}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= pageCount}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminTransactions;
